import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Fade,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  LocalShipping as DeliveryIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Timer as TimerIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEmpresa } from '../../context/EmpresaContext';

// Umbral de alerta: servicios que llevan más de este tiempo se marcan
const ALERTA_MINUTOS = 120; // 2 horas por defecto

const OrdenCard = ({ orden, estado, onView, onUpdate }) => {
  const navigate = useNavigate();
  const { simboloMoneda } = useEmpresa();
  const [expanded, setExpanded] = useState(false);
  const [tiempoVivo, setTiempoVivo] = useState('');
  const [minutosTranscurridos, setMinutosTranscurridos] = useState(0);

  const moneda = simboloMoneda || 'S/';
  const isDelivery = orden.tipo_servicio?.toLowerCase().includes('delivery');
  const isMaster = isDelivery && !orden.id_orden_padre;
  const isHija = isDelivery && !!orden.id_orden_padre;
  const entregada = isHija && [5, 8].includes(parseInt(orden.id_estado_servicio));

  // Estados "cerrados" (terminado, cancelado, revisado, liquidado)
  const estaCerrado = [5, 7, 8, 9, 10].includes(parseInt(orden.id_estado_servicio));
  // Estados "activos" (en camino, en proceso)
  const estaActivo = [3, 4, 6].includes(parseInt(orden.id_estado_servicio));

  const servicioNombre = orden.detalles?.[0]?.nombre_servicio || null;

  const formatCurrency = (amount) => `${moneda} ${parseFloat(amount || 0).toFixed(2)}`;
  const formatTime = (fecha) => new Date(fecha).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });

  // Timer en vivo para servicios activos
  useEffect(() => {
    const calcular = () => {
      const inicio = new Date(orden.fecha_orden);
      const fin = estaCerrado ? new Date(orden.fecha_actualizacion || orden.fecha_orden) : new Date();
      const diffMs = fin - inicio;
      const mins = Math.floor(diffMs / 60000);
      const hrs = Math.floor(mins / 60);
      const restMins = mins % 60;

      setMinutosTranscurridos(mins);
      if (hrs > 0) {
        setTiempoVivo(`${hrs}h ${restMins}m`);
      } else {
        setTiempoVivo(`${restMins}m`);
      }
    };

    calcular();

    // Solo actualizar en vivo si no está cerrado
    if (!estaCerrado) {
      const interval = setInterval(calcular, 30000); // cada 30s
      return () => clearInterval(interval);
    }
  }, [orden.fecha_orden, orden.fecha_actualizacion, estaCerrado]);

  const excedeTiempo = !estaCerrado && minutosTranscurridos > ALERTA_MINUTOS;

  const handleEdit = () => {
    if (isMaster) return navigate(`/home/delivery/${orden.id_orden_servicio}`);
    if (isHija) return navigate(`/home/delivery/${orden.id_orden_padre}`);
    navigate(`/home/ordenes/editar/${orden.id_orden_servicio}`);
  };

  const handleView = () => {
    if (isMaster || isHija) return navigate(`/home/delivery/${isMaster ? orden.id_orden_servicio : orden.id_orden_padre}`);
    onView(orden);
  };

  // Color del timer según estado
  const timerColor = estaCerrado ? '#94A3B8' : excedeTiempo ? '#EF4444' : estaActivo ? '#F59E0B' : '#94A3B8';
  const timerBg = estaCerrado ? '#F1F5F9' : excedeTiempo ? '#FEF2F2' : estaActivo ? '#FFFBEB' : '#F1F5F9';

  return (
    <Fade in timeout={300}>
      <Card sx={{
        mb: 1.5,
        borderRadius: 2.5,
        borderLeft: `5px solid ${isMaster ? '#1A237E' : isHija ? (entregada ? '#10B981' : '#C7D2FE') : estado.color}`,
        boxShadow: excedeTiempo ? '0 2px 12px rgba(239,68,68,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
        bgcolor: isMaster ? 'rgba(26,35,131,0.03)' : excedeTiempo ? 'rgba(239,68,68,0.02)' : '#fff',
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1A237E' }}>
                  #{orden.id_orden_servicio}
                </Typography>
                {isMaster && (
                  <Chip label="MASTER" size="small" icon={<DeliveryIcon sx={{ fontSize: 12 }} />}
                    sx={{ height: 20, fontSize: 9, fontWeight: 800, bgcolor: '#1A237E', color: '#fff',
                      '& .MuiChip-icon': { color: '#fff' } }} />
                )}
                {isHija && (
                  <Chip label={`Ruta ${orden.id_orden_padre}`} size="small"
                    sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: '#EEF2FF', color: '#1A237E' }} />
                )}
                <Chip label={formatTime(orden.fecha_orden)} size="small"
                  sx={{ height: 20, fontSize: 10, bgcolor: `${estado.color}15`, color: estado.color, fontWeight: 600 }} />
              </Box>
            </Box>

            {/* Acciones */}
            <Box sx={{ display: 'flex', gap: 0.3, ml: 0.5 }}>
              <Tooltip title={isMaster || isHija ? 'Ver ruta' : 'Ver'}>
                <IconButton size="small" onClick={handleView}
                  sx={{ width: 26, height: 26, bgcolor: '#E3F2FD', color: '#1565C0', '&:hover': { bgcolor: '#BBDEFB' } }}>
                  <SearchIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={handleEdit}
                  sx={{ width: 26, height: 26, bgcolor: '#EDE7F6', color: '#5E35B1', '&:hover': { bgcolor: '#D1C4E9' } }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              {!isMaster && (
                <Tooltip title="Cambiar estado">
                  <IconButton size="small" onClick={() => onUpdate(orden)}
                    sx={{ width: 26, height: 26, bgcolor: '#E8F5E9', color: '#2E7D32', '&:hover': { bgcolor: '#C8E6C9' } }}>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Expandir">
                <IconButton size="small" onClick={() => setExpanded(!expanded)}
                  sx={{ width: 26, height: 26, bgcolor: 'rgba(26,35,126,0.06)', color: '#1A237E',
                    transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                  <ExpandMoreIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Cliente o Ruta */}
          {isMaster ? (
            <Typography variant="caption" color="text.secondary" fontStyle="italic" sx={{ display: 'block', mb: 0.3 }}>
              Ruta de delivery — {orden.destinos?.length || '?'} destinos
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              <PersonIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
              <Typography variant="body2" fontWeight={500} noWrap>
                {orden.nombre_cliente} {orden.apellido_cliente}
              </Typography>
            </Box>
          )}

          {/* Servicio (visible sin expandir) */}
          {servicioNombre && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              <BuildIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
              <Typography variant="caption" fontWeight={600} color="#475569" noWrap>
                {servicioNombre}
              </Typography>
            </Box>
          )}

          {/* Detalle entrega (hijas) */}
          {isHija && orden.detalle_entrega && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.3, pl: 2.2 }}>
              {orden.detalle_entrega}
            </Typography>
          )}

          {/* Estado entrega para hijas */}
          {isHija && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              {entregada ? (
                <Chip icon={<CheckIcon sx={{ fontSize: 12 }} />} label="Entregado" size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#DCFCE7', color: '#16A34A',
                    '& .MuiChip-icon': { color: '#16A34A' } }} />
              ) : (
                <Chip icon={<PendingIcon sx={{ fontSize: 12 }} />} label="Pendiente" size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#FEF3C7', color: '#D97706',
                    '& .MuiChip-icon': { color: '#D97706' } }} />
              )}
              {orden.orden_secuencia && (
                <Typography variant="caption" color="text.secondary">Entrega #{orden.orden_secuencia}</Typography>
              )}
            </Box>
          )}

          {/* Fila: local + precio + timer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.8, gap: 0.5 }}>
            {/* Local */}
            {!isHija && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flex: 1, minWidth: 0 }}>
                <LocationIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {orden.nombre_local || '-'}
                </Typography>
              </Box>
            )}
            {isHija && <Box sx={{ flex: 1 }} />}

            {/* Precio */}
            {orden.precio_total > 0 && (
              <Chip label={formatCurrency(orden.precio_total)} size="small"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#1A237E', color: '#fff' }} />
            )}

            {/* Timer de duración */}
            <Tooltip title={estaCerrado
              ? `Duración total: ${tiempoVivo}`
              : excedeTiempo
                ? `Servicio lleva ${tiempoVivo} — excede el umbral de ${Math.floor(ALERTA_MINUTOS / 60)}h`
                : `Tiempo transcurrido: ${tiempoVivo}`}>
              <Chip
                icon={excedeTiempo ? <WarningIcon sx={{ fontSize: 12 }} /> : <TimerIcon sx={{ fontSize: 12 }} />}
                label={tiempoVivo}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: timerBg, color: timerColor,
                  '& .MuiChip-icon': { color: timerColor },
                  animation: excedeTiempo ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.6 },
                  },
                }}
              />
            </Tooltip>
          </Box>

          {/* Tracker */}
          {orden.trabajadores && orden.trabajadores.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: estado.color }}>
                {orden.trabajadores[0].nombre_trabajador?.[0]}
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {orden.trabajadores[0].nombre_trabajador} {orden.trabajadores[0].apellido_trabajador?.[0]}.
              </Typography>
            </Box>
          )}

          {/* Master: barra de progreso */}
          {isMaster && orden.destinos && orden.destinos.length > 0 && (() => {
            const total = orden.destinos.length;
            const entregados = orden.destinos.filter(d => d.entregado).length;
            const pct = Math.round((entregados / total) * 100);
            return (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" fontWeight={600} color="#1A237E">
                    {entregados}/{total} entregas
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color={pct === 100 ? '#10B981' : '#1A237E'}>
                    {pct}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ height: 6, borderRadius: 3, bgcolor: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: pct === 100 ? '#10B981' : '#1A237E' } }} />
              </Box>
            );
          })()}

          {/* Expandible */}
          <Collapse in={expanded} timeout="auto">
            <Divider sx={{ my: 1, borderColor: '#E2E8F0' }} />

            {orden.detalles && orden.detalles.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="#1A237E" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <BuildIcon sx={{ fontSize: 13 }} /> Servicios
                </Typography>
                <List dense disablePadding>
                  {orden.detalles.map((d, i) => (
                    <ListItem key={i} sx={{ py: 0.2, px: 0 }}>
                      <ListItemText
                        primary={<Typography variant="caption" fontWeight={500}>{d.nombre_servicio}</Typography>}
                        secondary={<Typography variant="caption" color="text.secondary">x{d.cantidad} — {formatCurrency(d.precio)}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {orden.trabajadores && orden.trabajadores.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="#1A237E" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 13 }} /> Tracker
                </Typography>
                {orden.trabajadores.map((t, i) => (
                  <Box key={i} sx={{ pl: 1 }}>
                    <Typography variant="caption" fontWeight={500}>
                      {t.nombre_trabajador} {t.apellido_trabajador}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t.telefono_trabajador} · Comisión: {formatCurrency(t.comision)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Tiempo detallado al expandir */}
            <Divider sx={{ my: 1, borderColor: '#E2E8F0' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimerIcon sx={{ fontSize: 14, color: timerColor }} />
              <Typography variant="caption" color="text.secondary">
                {estaCerrado ? 'Duración total' : 'Tiempo transcurrido'}: <strong style={{ color: timerColor }}>{tiempoVivo}</strong>
                {' · '}Inicio: {new Date(orden.fecha_orden).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {estaCerrado && orden.fecha_actualizacion && (
                  <> · Cierre: {new Date(orden.fecha_actualizacion).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>
                )}
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default OrdenCard;
